import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { ChatContextSource } from '@eluve/api-contract';

export type PopupState = {
  text: string;
  source: ChatContextSource;
  anchorRect: DOMRect;
};

type SelectableActions = {
  showPopup: (state: PopupState) => void;
  dismissPopup: () => void;
  selectFromPopup: () => void;
};

const SelectableActionsContext = createContext<SelectableActions | null>(null);
const SelectablePopupStateContext = createContext<PopupState | null>(null);

export const useSelectableActions = (): SelectableActions => {
  const ctx = useContext(SelectableActionsContext);
  if (!ctx) {
    throw new Error(
      'useSelectableActions must be used inside SelectableContextProvider',
    );
  }
  return ctx;
};

export const useSelectablePopupState = (): PopupState | null =>
  useContext(SelectablePopupStateContext);

type SelectableContextProviderProps = {
  onSelect: (payload: { text: string; source: ChatContextSource }) => void;
  children: ReactNode;
};

export const SelectableContextProvider: FC<SelectableContextProviderProps> = ({
  onSelect,
  children,
}) => {
  const [popupState, setPopupState] = useState<PopupState | null>(null);

  const actions = useMemo<SelectableActions>(
    () => ({
      showPopup: setPopupState,
      dismissPopup: () => setPopupState(null),
      selectFromPopup: () => {
        setPopupState((current) => {
          if (!current) return null;
          document.getSelection()?.removeAllRanges();
          onSelect({ text: current.text, source: current.source });
          return null;
        });
      },
    }),
    [onSelect],
  );

  return (
    <SelectableActionsContext.Provider value={actions}>
      <SelectablePopupStateContext.Provider value={popupState}>
        {children}
      </SelectablePopupStateContext.Provider>
    </SelectableActionsContext.Provider>
  );
};
